import React, { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import Trans from '@lib/components/Trans/Trans';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { IconName } from '@lib/components/Modal/enums';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  DocumentationValidationStatusEnum,
  useEdmDocumentationBulkUpdateValidationStatusMutation,
  useEdmDocumentationSkipRenewalMutation,
} from 'graphql-common';
import {
  useEdmDocumentationBulkFavoriteMutation,
  useEdmDocumentationBulkUnfavoriteMutation,
} from '@lib/graphql-common';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { APP_URLS } from 'constants/urls';
import useDocumentViewActions from './utils/useDocumentViewActions';
import DocumentViewPage from './DocumentViewPage';

export default function DocumentView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const {
    getUserAccessByCompany,
    getUserAccessByComponent,
    isFullAdmin,
  }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.Read;
  const isViewLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;
  // AI field-detection state (aiDetectionStatus / suggestedCodes) is gated on the
  // BE by DOCUMENTATION_GATE (documentation_management + ai_assistance). Requesting
  // it without both nullifies the whole resolver, so only select those fields when
  // the company has AiAssistance (Write), documentation isn't plan-locked, AND the
  // current user/team isn't individually restricted from AiAssistance.
  const isAiDetectionEnabled =
    getUserAccessByCompany(AccessComponentEnum.AiAssistance) ===
      AccessLevelEnum.Write &&
    getUserAccessByComponent(AccessComponentEnum.AiAssistance) ===
      AccessLevelEnum.Write &&
    !isViewLocked;
  const isSitesAvailable =
    getUserAccessByComponent(AccessComponentEnum.SitesManagement) !==
    AccessLevelEnum.LimitedRead;
  const isAssetsAvailable =
    getUserAccessByComponent(AccessComponentEnum.AssetsManagement) !==
    AccessLevelEnum.LimitedRead;
  const isTasksAvailable =
    getUserAccessByComponent(AccessComponentEnum.TasksManagement) !==
    AccessLevelEnum.LimitedRead;
  // The Accesses tab is a premium upgrade affordance when locked — hide it
  // entirely for non-Full-Admin Users since they can't act on the CTA. For
  // the same reason, restrict the Operational-scope tab to the (non-locked)
  // assets table only. (The History tab itself stays visible — its inner
  // "Views" sub-tab is gated separately inside DocumentViewHistory.)
  const restrictedLockedView = isViewLocked && !isFullAdmin;
  const isAccessesAvailable = isSitesAvailable && !restrictedLockedView;
  const isOperationalScopeAvailable = restrictedLockedView
    ? isAssetsAvailable
    : isSitesAvailable || isAssetsAvailable || isTasksAvailable;
  const {
    documentQueryHookResult,
    breadCrumbsData,
    loading,
    error,
    firstLoading,
    isDeleteModalOpen,
    onEdit,
    openDeleteModal,
    closeDeleteModal,
    onConfirmedDelete,
  } = useDocumentViewActions({ isViewLocked, isAiDetectionEnabled });

  const [bulkFavorite, { loading: favoriteLoading }] =
    useEdmDocumentationBulkFavoriteMutation({
      onError: (err) => showGraphQLErrors(err, t),
    });
  const [bulkUnfavorite, { loading: unfavoriteLoading }] =
    useEdmDocumentationBulkUnfavoriteMutation({
      onError: (err) => showGraphQLErrors(err, t),
    });

  const [bulkUpdateValidationStatus, { loading: validationStatusLoading }] =
    useEdmDocumentationBulkUpdateValidationStatusMutation({
      onError: (err) => showGraphQLErrors(err, t),
    });

  const [skipRenewal, { loading: skipRenewalLoading }] =
    useEdmDocumentationSkipRenewalMutation({
      onError: (err) => showGraphQLErrors(err, t),
    });

  const [isSkipRenewalModalOpen, setIsSkipRenewalModalOpen] = useState(false);
  const openSkipRenewalModal = useCallback(
    () => setIsSkipRenewalModalOpen(true),
    [],
  );
  const closeSkipRenewalModal = useCallback(
    () => setIsSkipRenewalModalOpen(false),
    [],
  );

  const [activityRefetchKey, setActivityRefetchKey] = useState(0);

  const onValidationStatusChange = useCallback(
    (docId: string, newStatus: DocumentationValidationStatusEnum) => {
      bulkUpdateValidationStatus({
        variables: {
          ids: [docId],
          attributes: { validationStatus: newStatus },
        },
        onCompleted: () => {
          toast({ content: t('update-document-success') });
          documentQueryHookResult.refetch();
          setActivityRefetchKey((prev) => prev + 1);
        },
      });
    },
    [bulkUpdateValidationStatus, documentQueryHookResult, t],
  );

  const isFavorited = documentQueryHookResult.data?.favorited ?? false;
  const isFavoriteLoading = favoriteLoading || unfavoriteLoading;

  const onFavoriteToggle = useCallback(() => {
    const item = documentQueryHookResult.data;
    if (!item?.id) return;
    const mutation = isFavorited ? bulkUnfavorite : bulkFavorite;
    const successMessage = isFavorited
      ? t('remove-from-favorites-success')
      : t('add-to-favorites-success');
    mutation({
      variables: { ids: [item.id] },
      update: (cache) => {
        cache.modify({
          id: cache.identify(item),
          fields: {
            favorited: () => !isFavorited,
          },
        });
      },
      onCompleted: () => {
        toast({ content: successMessage });
      },
    });
  }, [bulkFavorite, bulkUnfavorite, documentQueryHookResult, isFavorited, t]);

  const onCreateVersion = useCallback(() => {
    navigate(
      APP_URLS.dashboard.documentation.createVersion.getDynamicPath({
        pathParts: { id },
      }),
    );
  }, [id, navigate]);

  const onConfirmSkipRenewal = useCallback(() => {
    if (!id) return;
    skipRenewal({
      variables: { id },
      onCompleted: () => {
        closeSkipRenewalModal();
        toast({ content: t('skip-renewal-success') });
        documentQueryHookResult.refetch();
        setActivityRefetchKey((prev) => prev + 1);
      },
    });
  }, [closeSkipRenewalModal, documentQueryHookResult, id, skipRenewal, t]);

  if (error) {
    return (
      <ErrorPage
        status={getStatusFromError(error)}
        description={getDescriptionFromError(error)}
      />
    );
  }

  if (firstLoading) {
    return <CircleLoader />;
  }

  return (
    <>
      <BreadCrumbs data={breadCrumbsData} />
      <DocumentViewPage
        documentQueryHookResult={documentQueryHookResult}
        isAccessesAvailable={isAccessesAvailable}
        isAssetsAvailable={isAssetsAvailable}
        isFavorited={isFavorited}
        isFavoriteLoading={isFavoriteLoading}
        isFullAdmin={isFullAdmin}
        isOperationalScopeAvailable={isOperationalScopeAvailable}
        isSitesAvailable={isSitesAvailable}
        isTasksAvailable={isTasksAvailable}
        isViewLocked={isViewLocked}
        isViewOnly={isViewOnly}
        isAiDetectionEnabled={isAiDetectionEnabled}
        loading={loading}
        onCreateVersion={onCreateVersion}
        onEdit={onEdit}
        onDelete={openDeleteModal}
        onFavoriteToggle={onFavoriteToggle}
        onSkipRenewal={openSkipRenewalModal}
        onValidationStatusChange={onValidationStatusChange}
        validationStatusLoading={validationStatusLoading}
        activityRefetchKey={activityRefetchKey}
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="delete-document-text" />}
        icon={IconName.Delete}
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={onConfirmedDelete}
        title={t('are-you-sure')}
        useProgress
      />
      <ConfirmationModal
        confirmButtonLabel={t('skip-this-renewal')}
        description={<Trans i18nKey="skip-current-renewal-text" />}
        icon={IconName.Warning}
        isOpen={isSkipRenewalModalOpen}
        loading={skipRenewalLoading}
        onClose={closeSkipRenewalModal}
        onConfirm={onConfirmSkipRenewal}
        title={t('skip-current-renewal-title')}
        useProgress
      />
    </>
  );
}
